import { useTranslation } from "react-i18next";
import type { Zone, Slot, Rect, ViewState } from "./warehouse-types";
import type { ResizeHandle } from "./useWarehouse";
import { DEFAULT_FLOOR_PLAN_RECT } from "./useWarehouse";

interface CanvasLayerProps {
  floorPlanUrl: string | null;
  floorPlanRect?: Rect;
  floorPlanEditable: boolean;
  zones: Zone[];
  viewState: ViewState;
  currentRect: Rect | null;
  isDrawing: boolean;
  selectedZoneId: string | null;
  selectedSlotId: string | null;
  dragSlotPreview: { x: number; y: number } | null;
  onZoneClick: (zoneId: string) => void;
  onZoneSelect: (zoneId: string) => void;
  onSlotSelect: (slotId: string) => void;
  onStartResize: (handle: ResizeHandle, rect: Rect) => void;
  onStartDrag: (clientX: number, clientY: number, rect: Rect) => void;
}

export function CanvasLayer({
  floorPlanUrl,
  floorPlanRect,
  floorPlanEditable,
  zones,
  viewState,
  currentRect,
  isDrawing,
  selectedZoneId,
  selectedSlotId,
  dragSlotPreview,
  onZoneClick,
  onZoneSelect,
  onSlotSelect,
  onStartResize,
  onStartDrag,
}: CanvasLayerProps) {
  const { t } = useTranslation();
  const { scale, offset, mode, zoneId: activeZoneId } = viewState;

  return (
    <div
      data-canvas-layer
      className="absolute origin-top-left"
      style={{
        transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
        width: "1200px",
        height: "800px",
      }}
    >
      {/* Floor plan image */}
      {floorPlanUrl && (
        <FloorPlanImage
          url={floorPlanUrl}
          rect={floorPlanRect ?? DEFAULT_FLOOR_PLAN_RECT}
          editable={floorPlanEditable}
          onStartDrag={onStartDrag}
          onStartResize={onStartResize}
        />
      )}

      {/* Grid background when no floor plan */}
      {!floorPlanUrl && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />
      )}

      {/* Zones — made click-through while editing the floor plan so the
          image beneath can be dragged/resized */}
      <div style={{ pointerEvents: floorPlanEditable ? "none" : "auto" }}>
      {zones.map((zone) => (
        <ZoneRect
          key={zone.id}
          zone={zone}
          isActive={mode === "zone" && activeZoneId === zone.id}
          isSelected={selectedZoneId === zone.id}
          isInZoneMode={mode === "zone"}
          isCurrentZone={mode === "zone" && activeZoneId === zone.id}
          onDoubleClick={() => {
            // In warehouse mode: zoom to zone
            // In zone mode: if clicking another zone, zoom to that zone
            if (
              mode === "warehouse" ||
              (mode === "zone" && activeZoneId !== zone.id)
            ) {
              onZoneClick(zone.id);
            }
          }}
          onClick={() => {
            // In warehouse mode: select zone
            // In zone mode clicking another zone: zoom to that zone directly
            // In zone mode clicking the current zone: select it (for moving/resizing)
            if (mode === "warehouse") {
              onZoneSelect(zone.id);
            } else if (mode === "zone" && activeZoneId !== zone.id) {
              // Single click on another zone in zone mode = zoom to it
              onZoneClick(zone.id);
            } else if (mode === "zone" && activeZoneId === zone.id) {
              // Single click on the current zone in zone mode = select it (for moving)
              onZoneSelect(zone.id);
            }
          }}
          onStartResize={(handle) => onStartResize(handle, zone.rect)}
          onStartDrag={(clientX, clientY) =>
            onStartDrag(clientX, clientY, zone.rect)
          }
          showSlots={mode === "zone" && activeZoneId === zone.id}
          selectedSlotId={selectedSlotId}
          onSlotSelect={onSlotSelect}
          onSlotStartResize={(handle, rect) => onStartResize(handle, rect)}
          onSlotStartDrag={(clientX, clientY, rect) =>
            onStartDrag(clientX, clientY, rect)
          }
        />
      ))}
      </div>

      {/* Drawing preview for zones */}
      {isDrawing && currentRect && mode === "warehouse" && (
        <div
          className="pointer-events-none absolute rounded border-2 border-dashed"
          style={{
            left: currentRect.x,
            top: currentRect.y,
            width: currentRect.width,
            height: currentRect.height,
            borderColor: "rgb(45, 212, 191)",
            backgroundColor: "rgba(45, 212, 191, 0.2)",
          }}
        >
          <div className="absolute -top-6 left-0 whitespace-nowrap rounded bg-[#1a1d24] px-2 py-0.5 text-xs text-zinc-100">
            {Math.round(currentRect.width)} x {Math.round(currentRect.height)}
          </div>
        </div>
      )}

      {/* Drag slot preview */}
      {dragSlotPreview && mode === "zone" && (
        <div
          className="pointer-events-none absolute rounded-md border-2 border-dashed border-blue-400 bg-blue-400/20"
          style={{
            left: dragSlotPreview.x - 60,
            top: dragSlotPreview.y - 40,
            width: 120,
            height: 80,
          }}
        >
          <div className="absolute left-1.5 top-1.5 rounded bg-blue-500/80 px-2 py-0.5 text-xs font-semibold text-white">
            {t("warehouseMap.newSlot")}
          </div>
        </div>
      )}
    </div>
  );
}

// Floor plan image - draggable & resizable when the floor plan tool is active
interface FloorPlanImageProps {
  url: string;
  rect: Rect;
  editable: boolean;
  onStartDrag: (clientX: number, clientY: number, rect: Rect) => void;
  onStartResize: (handle: ResizeHandle, rect: Rect) => void;
}

function FloorPlanImage({
  url,
  rect,
  editable,
  onStartDrag,
  onStartResize,
}: FloorPlanImageProps) {
  return (
    <div
      className={`absolute ${
        editable
          ? "cursor-move rounded ring-2 ring-teal-300/70"
          : "pointer-events-none"
      }`}
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
      }}
      onPointerDown={(e) => {
        if (!editable) return;
        e.stopPropagation();
        onStartDrag(e.clientX, e.clientY, rect);
      }}
    >
      <img
        src={url}
        alt="Warehouse floor plan"
        className="pointer-events-none h-full w-full select-none object-contain"
        crossOrigin="anonymous"
        draggable={false}
      />
      {editable && (
        <ResizeHandles onStartResize={(handle) => onStartResize(handle, rect)} />
      )}
    </div>
  );
}

// Resize handles component
function ResizeHandles({
  onStartResize,
}: {
  onStartResize: (handle: ResizeHandle) => void;
}) {
  const handles: { handle: ResizeHandle; className: string; cursor: string }[] = [
    { handle: "nw", className: "-left-1 -top-1", cursor: "nwse-resize" },
    { handle: "n", className: "left-1/2 -translate-x-1/2 -top-1", cursor: "ns-resize" },
    { handle: "ne", className: "-right-1 -top-1", cursor: "nesw-resize" },
    { handle: "e", className: "-right-1 top-1/2 -translate-y-1/2", cursor: "ew-resize" },
    { handle: "se", className: "-right-1 -bottom-1", cursor: "nwse-resize" },
    { handle: "s", className: "left-1/2 -translate-x-1/2 -bottom-1", cursor: "ns-resize" },
    { handle: "sw", className: "-left-1 -bottom-1", cursor: "nesw-resize" },
    { handle: "w", className: "-left-1 top-1/2 -translate-y-1/2", cursor: "ew-resize" },
  ];

  return (
    <>
      {handles.map(({ handle, className, cursor }) => (
        <div
          key={handle}
          className={`absolute z-10 h-3 w-3 rounded-sm border border-gray-400 bg-white shadow-sm hover:bg-blue-100 [@media(pointer:coarse)]:h-5 [@media(pointer:coarse)]:w-5 [@media(pointer:coarse)]:before:absolute [@media(pointer:coarse)]:before:-inset-3 [@media(pointer:coarse)]:before:content-[''] ${className}`}
          style={{ cursor }}
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onStartResize(handle);
          }}
        />
      ))}
    </>
  );
}

interface ZoneRectProps {
  zone: Zone;
  isActive: boolean;
  isSelected: boolean;
  isInZoneMode: boolean;
  isCurrentZone: boolean;
  onClick: () => void;
  onDoubleClick: () => void;
  onStartResize: (handle: ResizeHandle) => void;
  onStartDrag: (clientX: number, clientY: number) => void;
  showSlots: boolean;
  selectedSlotId: string | null;
  onSlotSelect: (slotId: string) => void;
  onSlotStartResize: (handle: ResizeHandle, rect: Rect) => void;
  onSlotStartDrag: (clientX: number, clientY: number, rect: Rect) => void;
}

function ZoneRect({
  zone,
  isActive,
  isSelected,
  isInZoneMode,
  isCurrentZone,
  onClick,
  onDoubleClick,
  onStartResize,
  onStartDrag,
  showSlots,
  selectedSlotId,
  onSlotSelect,
  onSlotStartResize,
  onSlotStartDrag,
}: ZoneRectProps) {
  const { t } = useTranslation();
  // Determine if this zone should be clickable
  // - In warehouse mode: always clickable
  // - In zone mode: only clickable if it's not the current zone (to allow switching)
  const isClickable = !isInZoneMode || !isCurrentZone;

  return (
    <div
      className={`absolute rounded-lg border-2 transition-colors duration-150 ${
        isSelected
          ? "cursor-move border-teal-300 shadow-lg shadow-teal-400/40"
          : isActive
            ? "border-teal-400 shadow-lg shadow-teal-400/20"
            : isClickable
              ? "cursor-pointer border-teal-400/60 hover:border-teal-400 hover:shadow-md hover:shadow-teal-400/10"
              : "border-teal-400/40"
      }`}
      style={{
        left: zone.rect.x,
        top: zone.rect.y,
        width: zone.rect.width,
        height: zone.rect.height,
        backgroundColor: zone.color,
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        // Start dragging if selected (works in both warehouse and zone mode)
        if (isSelected) {
          onStartDrag(e.clientX, e.clientY);
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick();
      }}
    >
      {/* Zone label */}
      <div
        className={`absolute left-2 top-2 rounded-md px-2.5 py-1 text-sm font-bold shadow-sm ${
          isActive
            ? "bg-teal-400 text-[#0d0f15]"
            : isSelected
              ? "bg-teal-500 text-white"
              : "bg-teal-500/90 text-white"
        }`}
      >
        {t("warehouseMap.zoneLabel", { name: zone.name })}
      </div>

      {/* Slot count badge (when not showing slots) */}
      {!showSlots && zone.slots.length > 0 && (
        <div className="absolute bottom-2 right-2 rounded-full bg-[#1a1d24]/90 px-2 py-0.5 text-xs text-zinc-400">
          {t("warehouseMap.slotCount", { count: zone.slots.length })}
        </div>
      )}

      {/* Resize handles (when selected, in either mode) */}
      {isSelected && <ResizeHandles onStartResize={onStartResize} />}

      {/* Slots (only show when zone is active) */}
      {showSlots &&
        zone.slots.map((slot) => (
          <SlotRect
            key={slot.id}
            slot={slot}
            parentRect={zone.rect}
            isSelected={slot.id === selectedSlotId}
            onSelect={() => onSlotSelect(slot.id)}
            onStartResize={(handle) => onSlotStartResize(handle, slot.rect)}
            onStartDrag={(clientX, clientY) =>
              onSlotStartDrag(clientX, clientY, slot.rect)
            }
          />
        ))}
    </div>
  );
}

interface SlotRectProps {
  slot: Slot;
  parentRect: Rect;
  isSelected: boolean;
  onSelect: () => void;
  onStartResize: (handle: ResizeHandle) => void;
  onStartDrag: (clientX: number, clientY: number) => void;
}

function SlotRect({
  slot,
  parentRect,
  isSelected,
  onSelect,
  onStartResize,
  onStartDrag,
}: SlotRectProps) {
  const { t } = useTranslation();
  // Convert absolute position to relative position within zone
  const relativeX = slot.rect.x - parentRect.x;
  const relativeY = slot.rect.y - parentRect.y;

  const slotColor = slot.color || "rgba(96, 165, 250, 0.35)";

  return (
    <div
      className={`absolute cursor-pointer rounded-md border-2 transition-colors duration-150 ${
        isSelected
          ? "border-blue-400 shadow-lg shadow-blue-400/30"
          : "border-blue-400/50 hover:border-blue-400 hover:shadow-md hover:shadow-blue-400/20"
      }`}
      style={{
        left: relativeX,
        top: relativeY,
        width: slot.rect.width,
        height: slot.rect.height,
        backgroundColor: isSelected
          ? slotColor.replace("0.35", "0.5")
          : slotColor,
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        // Start dragging if already selected
        if (isSelected) {
          onStartDrag(e.clientX, e.clientY);
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      <div
        className={`absolute left-1.5 top-1.5 rounded px-2 py-0.5 text-xs font-semibold ${
          isSelected ? "bg-blue-500 text-white" : "bg-blue-500/80 text-white"
        }`}
      >
        {slot.name}
      </div>

      {/* Item count */}
      {slot.items.length > 0 && (
        <div className="absolute bottom-1.5 right-1.5 rounded-full bg-[#1a1d24]/90 px-1.5 py-0.5 text-[10px] text-zinc-400">
          {t("warehouseMap.itemCount", { count: slot.items.length })}
        </div>
      )}

      {/* Resize handles (when selected) */}
      {isSelected && <ResizeHandles onStartResize={onStartResize} />}
    </div>
  );
}
